// SMS credit / segment calculation.
//
// Mobile Message (and every SMS carrier) bills per message "part", not per
// message. A plain-text SMS fits 160 characters in one part; once it goes over
// that, the carrier splits it into concatenated parts of 153 chars each (the
// extra 7 chars per part are used for the join header). If the message contains
// any character outside the GSM-7 alphabet (most emoji, curly quotes, etc.) the
// whole message is sent as Unicode, where a single part is only 70 chars and
// concatenated parts are 67 chars. Each part = 1 credit.

// Characters in the GSM-7 default alphabet that count as a single character.
const GSM7_BASIC =
  "@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞ ÆæßÉ !\"#¤%&'()*+,-./0123456789:;<=>?" +
  "¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà";

// GSM-7 "extended" characters. These are valid in a GSM message but each one
// occupies TWO character slots (they are sent with an escape prefix).
const GSM7_EXTENDED = "^{}\\[~]|€";

// Does the message fit in the GSM-7 alphabet at all? If not, it is Unicode.
function isGsm7(text) {
  for (const ch of text) {
    if (GSM7_BASIC.indexOf(ch) === -1 && GSM7_EXTENDED.indexOf(ch) === -1) {
      return false;
    }
  }
  return true;
}

// Effective character count. For GSM-7, extended chars count double. For
// Unicode we count code points (so an emoji made of one code point = 1, but
// characters outside the BMP are counted as 2 UTF-16 units, matching how
// carriers meter them).
function countUnits(text, gsm7) {
  if (gsm7) {
    let units = 0;
    for (const ch of text) {
      units += GSM7_EXTENDED.indexOf(ch) === -1 ? 1 : 2;
    }
    return units;
  }
  // Unicode / UCS-2: metered in 16-bit units.
  return text.length;
}

/**
 * Work out how many SMS credits (parts) a single message costs.
 * @param {string} text - the message body as typed
 * @returns {{ chars: number, credits: number, encoding: "GSM-7"|"Unicode", perPart: number, units: number }}
 */
export function smsInfo(text) {
  const body = text || "";
  const gsm7 = isGsm7(body);
  const encoding = gsm7 ? "GSM-7" : "Unicode";
  const units = countUnits(body, gsm7);

  const singleLimit = gsm7 ? 160 : 70;
  const multiLimit = gsm7 ? 153 : 67;

  let credits;
  if (units === 0) credits = 0;
  else if (units <= singleLimit) credits = 1;
  else credits = Math.ceil(units / multiLimit);

  return {
    chars: body.length,
    units,
    credits,
    encoding,
    perPart: credits <= 1 ? singleLimit : multiLimit,
  };
}

/**
 * Credits per message (parts). Convenience wrapper.
 */
export function smsCredits(text) {
  return smsInfo(text).credits;
}

// Mirrors core.services.brevo_service.send_twilio_sms's opt-out handling
// (Collings-CRM-BE): every marketing SMS that actually goes out either has the
// %%SMS_STOP%% placeholder swapped for a real per-recipient link, or - if the
// agent didn't add one - gets a "\n\nUnsubscribe: <link>" clause appended. The
// composer must price/measure the message that actually sends, not just what
// was typed, or the credit estimate silently undercounts (CRM-2026-00002).
export const SMS_STOP_PLACEHOLDER = "%%SMS_STOP%%";
const STOP_CLAUSE = "\n\nUnsubscribe: ";
// https://crm.collings.com.au (27) + /s/ (3) + a 10-char opaque code.
const OPTOUT_URL_LENGTH = "https://crm.collings.com.au/s/".length + 10;

/**
 * The body as it will actually be sent, once the auto/placeholder opt-out
 * link is applied - same-length stand-in URL, since the real one is only
 * known per-recipient at send time.
 */
export function withOptOut(text) {
  const placeholderUrl = "x".repeat(OPTOUT_URL_LENGTH);
  // The link is always the last line now, wherever the agent put the
  // placeholder (Simon Abbott, Monday 13392775822, 30 Oct 2026), so the
  // placeholder comes out and the clause goes on the end, as the backend does.
  const body = (text || "").split(SMS_STOP_PLACEHOLDER).join("")
    .replace(/[ \t]{2,}/g, " ")
    .replace(/\n{3,}/g, "\n\n");
  const trimmed = body.trimEnd();
  return trimmed ? `${trimmed}${STOP_CLAUSE}${placeholderUrl}` : body;
}

/** The body with the unsubscribe placeholder on the end, on its own line -
 *  where the "Insert unsubscribe link" button puts it (Monday 13392775822).
 *  Unchanged if it is already in there. */
export function appendStopPlaceholder(text) {
  const body = text || "";
  if (body.includes(SMS_STOP_PLACEHOLDER)) return body;
  const trimmed = body.trimEnd();
  return trimmed ? `${trimmed}\n\n${SMS_STOP_PLACEHOLDER}` : SMS_STOP_PLACEHOLDER;
}

/** The text with the own-number opening line on top (core.services.sms_text
 *  .with_own_number_intro), so the composer prices what actually sends. */
export function withSmsIntro(text, intro) {
  const body = text || "";
  if (!intro) return body;
  const key = (s) => foldPunctuation(s).toLowerCase().trim().replace(/\.$/, "");
  if (key(body).startsWith(key(intro))) return body;
  const rest = body.trimStart();
  return rest ? `${intro}\n\n${rest}` : intro;
}

// Mirrors communications/services/utm.py: every link to one of our own hosts
// leaves with ?utm_source=sms&utm_medium=sms&utm_campaign=crm_c<id> appended so
// the sign-ups and enquiries it produces can be credited back to the campaign
// (Analytics > CRM > Campaign Leads). That is roughly 52 billed characters per
// link, which is enough to push a 160-character text into a second credit — so
// the composer has to price the tagged message, not the typed one.
const TRACKING_SUFFIX_LENGTH =
  "?utm_source=sms&utm_medium=sms&utm_campaign=crm_c".length + 3; // + campaign id

// Our own hosts, matching the backend allowlist. Third-party links (a bank's
// calculator, realestate.com.au) are never tagged, so they cost nothing extra.
const TRACKED_HOST_RE = /^https?:\/\/[^/\s]*(collings\.com\.au|zaltech\.studio)/i;
// The opt-out short link is deliberately never tagged.
const OPTOUT_PATH_RE = /\/s\/|unsubscribe|opt-?out/i;
const URL_RE = /https?:\/\/[^\s]+/gi;

/**
 * The body with the tracking parameters the backend will add, so the credit
 * estimate matches what the recipient's phone actually receives.
 */
export function withTracking(text) {
  const body = text || "";
  return body.replace(URL_RE, (url) => {
    if (!TRACKED_HOST_RE.test(url)) return url;
    if (OPTOUT_PATH_RE.test(url)) return url;
    if (/[?&]utm_source=/i.test(url)) return url; // already tagged by hand
    return url + "x".repeat(TRACKING_SUFFIX_LENGTH);
  });
}

// Mirrors communications/services/link_shortener.py: since 28 Aug every long
// link of ours in an outgoing TEXT is replaced with https://<host>/l/<code>
// before it reaches the provider (CG: "Can we please reduce the characters used
// by using a link shortener like bitly. We are using way to many characters and
// we can reduce them"). A preferences link is a ~290-character signed token and
// our own links pick up another ~52 of tracking; both land at 37 characters.
// The composer has to price the SHORTENED message or it now over-quotes by
// three credits a text, which is the same lie as the old undercount, backwards.
const SHORT_LINK_LENGTH = "https://crm.collings.com.au/l/".length + 7;
// Only worth it when it actually saves characters - matches the backend's own
// rule, so a short link like collings.com.au/portal stays readable in both.
const SHORTEN_THRESHOLD = SHORT_LINK_LENGTH + 8;

/**
 * The body with our own long links collapsed to short ones, as the backend
 * will send it. Third-party links and the opt-out link are never shortened.
 */
export function withShortLinks(text) {
  const body = text || "";
  return body.replace(URL_RE, (raw) => {
    let url = raw;
    let trailing = "";
    while (url && ".,;:!?)]}'\"".includes(url[url.length - 1])) {
      trailing = url[url.length - 1] + trailing;
      url = url.slice(0, -1);
    }
    if (!TRACKED_HOST_RE.test(url)) return raw;
    if (OPTOUT_PATH_RE.test(url)) return raw;
    if (/\/l\//.test(url)) return raw; // already a short link
    if (url.length <= SHORTEN_THRESHOLD) return raw;
    return "x".repeat(SHORT_LINK_LENGTH) + trailing;
  });
}

// Mirrors core/services/sms_text.py normalise_sms_text: the backend folds smart
// punctuation to plain ASCII before every text goes out, so a pasted en dash
// ("$400,000–$600,000") or curly apostrophe is a GSM-7 character by the time
// it is billed, not a reason to price the whole message as Unicode.
const PUNCTUATION_FOLDS = [
  [/[‘’‚‛′]/g, "'"],
  [/[“”„″]/g, '"'],
  [/[–—―−]/g, "-"],
  [/…/g, "..."],
  [/[   ]/g, " "],
  [/​/g, ""],
];

export function foldPunctuation(text) {
  return PUNCTUATION_FOLDS.reduce((out, [re, plain]) => out.replace(re, plain), text || "");
}

/**
 * The message exactly as it will be billed: punctuation folded, link tracking
 * applied, then shortening, then the opt-out clause - the same order the
 * backend runs them in (comms_engine.send_communication). Use this everywhere a
 * credit count is shown; measuring the typed body gets all of them wrong.
 */
export function asSent(text) {
  return withOptOut(withShortLinks(withTracking(foldPunctuation(text))));
}

// The longest text the SMS provider (Mobile Message) will take: 10 parts of 153
// characters. Anything longer is refused and never arrives (Genneva,
// CRM-2026-00125: "When sending a test sms to my mobile number i am not
// receiving the test sms" - her text was 1,638 characters). The account does
// not send Unicode, so the gateway strips an emoji rather than shrinking the
// parts, and the limit is the same either way. Mirrors SMS_MAX_CHARS in
// core/services/sms_text.py.
export const SMS_MAX_CHARS = 1530;

/**
 * How many of the provider's characters an as-sent text uses: characters the
 * gateway would strip don't count, GSM-7 extended ones count twice.
 */
export function smsLength(text) {
  let units = 0;
  for (const ch of text || "") {
    if (GSM7_EXTENDED.indexOf(ch) !== -1) units += 2;
    else if (GSM7_BASIC.indexOf(ch) !== -1) units += 1;
  }
  return units;
}
